import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, UserPlus, Clock, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useBranding, brandingAssetUrl } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import InstallBanner from '../components/InstallBanner';

export default function SignupPage() {
  const { signup } = useAuth();
  const { branding } = useBranding();
  const [params] = useSearchParams();
  const inviteCode = params.get('invite');
  const redirectTo = params.get('redirect'); // iter125 — preserve /s/:encoded scan landing
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !phone || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await signup(name, phone, password, inviteCode || '');
      // Stash invite code so JoinGroupPage / Dashboard can act on it after admin-approval login
      if (inviteCode) {
        try { localStorage.setItem('pending_invite_code', inviteCode); } catch { /* ignore */ }
      }
      // iter125 — persist redirect target so post-login bounce lands on the original /s/ scan URL
      if (redirectTo && redirectTo.startsWith('/')) {
        try { localStorage.setItem('pending_redirect_after_login', redirectTo); } catch { /* ignore */ }
      }
      setSubmitted(true);
      toast.success('Account created! Waiting for admin approval.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="flex-1 flex flex-col justify-center items-center px-6 py-12">
          <div className="w-full max-w-sm mb-4">
            <InstallBanner
              tone="hero"
              storageKey="install_banner_dismissed_signup"
              title="Want it on your home screen?"
              subtitle="While the admin reviews your account, pin SplitEasy like a real app — opens instantly, works offline."
              testId="signup-install-banner"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-sm"
          >
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-3">Account Created!</h2>
            <p className="text-muted-foreground mb-6" data-testid="pending-message">
              Your account is pending admin approval. You'll be able to login once the admin confirms your account.
            </p>
            <Link to="/login">
              <Button className="rounded-full w-full h-12 text-lg font-bold" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          </motion.div>
        </div>
        <div className="h-2 bg-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Logo */}
          <div className="mb-8">
            {branding.logo_url && (
              <img
                src={brandingAssetUrl(branding.logo_url)}
                alt={branding.app_name}
                className="w-14 h-14 rounded-2xl object-contain mb-3"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <h1 className="font-display text-4xl font-bold tracking-tight">
              {branding.app_name || 'SplitEasy'}
            </h1>
            <p className="text-muted-foreground mt-2">
              Create your account
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-semibold uppercase tracking-wide">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="signup-name-input"
                className="h-14 text-lg border-2 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-semibold uppercase tracking-wide">
                Phone Number
              </Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="9820030046"
                  data-testid="signup-phone-input"
                  className="h-14 text-lg border-2 rounded-xl pl-12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wide">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  data-testid="signup-password-input"
                  className="h-14 text-lg border-2 rounded-xl pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-btn"
              className="w-full h-14 text-lg font-bold uppercase tracking-wider rounded-full"
            >
              {loading ? (
                <span className="animate-pulse">Creating account...</span>
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  Sign Up
                </>
              )}
            </Button>
          </form>

          {/* Login link */}
          <p className="mt-8 text-center text-muted-foreground">
            Already have an account?{' '}
            <Link 
              to="/login" 
              data-testid="login-link"
              className="text-primary font-semibold hover:underline"
            >
              Login
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Bottom decoration */}
      <div className="h-2 bg-primary" />
    </div>
  );
}
